// src/components/Filiacao/FormularioFiliacaoPublico.tsx
// UPDATED: 2026-03-25 — v3.1
// ✅ FIX: payload agora inclui complemento e dataInicioAtividade
// ✅ MANTIDO: todos os outros comportamentos idênticos

import React, { useEffect, useState, useRef } from 'react';
import { ChevronRight, ChevronLeft, Search } from 'lucide-react';
import UploadDocumentos from './UploadDocumentos';
import ConfirmacaoFiliacao from './ConfirmacaoFiliacao';
import { API_BASE } from '../../utils/apiConfig';
import { finishFiliationTracking, trackFiliationEvent } from '../../services/filiationAnalytics';
const BRAND = {
  gold: '#D4AF37',
  goldDeep: '#B88917',
  charcoal: '#1F2328',
};

const ESTADOS_BR = [
  { uf: 'AC', nome: 'Acre' },          { uf: 'AL', nome: 'Alagoas' },
  { uf: 'AP', nome: 'Amapá' },         { uf: 'AM', nome: 'Amazonas' },
  { uf: 'BA', nome: 'Bahia' },         { uf: 'CE', nome: 'Ceará' },
  { uf: 'DF', nome: 'Distrito Federal' }, { uf: 'ES', nome: 'Espírito Santo' },
  { uf: 'GO', nome: 'Goiás' },         { uf: 'MA', nome: 'Maranhão' },
  { uf: 'MT', nome: 'Mato Grosso' },   { uf: 'MS', nome: 'Mato Grosso do Sul' },
  { uf: 'MG', nome: 'Minas Gerais' },  { uf: 'PA', nome: 'Pará' },
  { uf: 'PB', nome: 'Paraíba' },       { uf: 'PR', nome: 'Paraná' },
  { uf: 'PE', nome: 'Pernambuco' },    { uf: 'PI', nome: 'Piauí' },
  { uf: 'RJ', nome: 'Rio de Janeiro' }, { uf: 'RN', nome: 'Rio Grande do Norte' },
  { uf: 'RS', nome: 'Rio Grande do Sul' }, { uf: 'RO', nome: 'Rondônia' },
  { uf: 'RR', nome: 'Roraima' },       { uf: 'SC', nome: 'Santa Catarina' },
  { uf: 'SP', nome: 'São Paulo' },     { uf: 'SE', nome: 'Sergipe' },
  { uf: 'TO', nome: 'Tocantins' },
];


interface DadosEtapa1 {
  nomeSolicitante: string;
  cpf: string;
  cnpj: string;
  nomeFantasia: string;
  dataNascimento: string;
  cep: string;
  endereco: string;
  complemento: string;
  cidade: string;
  estado: string;
  telefone: string;
  email: string;
  identidadeInternacional: string;
}

interface DadosEtapa2 {
  funcao: string;
  dataInicioAtividade: string;
  experienciaAnos: number;
  especializacoes: string;
}

interface DadosCompletos {
  etapa1: DadosEtapa1;
  etapa2: DadosEtapa2;
  documentos: File[];
}

const FILIACAO_DRAFT_KEY = 'filiacao-publica-rascunho-v1';

const dadosIniciais = (): DadosCompletos => ({
  etapa1: {
    nomeSolicitante: '', cpf: '', cnpj: '', nomeFantasia: '', dataNascimento: '',
    cep: '', endereco: '', complemento: '', cidade: '', estado: '', telefone: '',
    email: '', identidadeInternacional: '',
  },
  etapa2: {
    funcao: '', dataInicioAtividade: '', experienciaAnos: 0, especializacoes: '',
  },
  documentos: [],
});

const FormularioFiliacaoPublico: React.FC = () => {
  const [etapaAtual, setEtapaAtual] = useState<1 | 2 | 3>(1);
  const [dados, setDados] = useState<DadosCompletos>(() => {
    const initial = dadosIniciais();
    if (typeof window === 'undefined') return initial;
    try {
      const saved = JSON.parse(localStorage.getItem(FILIACAO_DRAFT_KEY) || 'null');
      if (!saved) return initial;
      return {
        etapa1: { ...initial.etapa1, ...(saved.etapa1 ?? {}) },
        etapa2: { ...initial.etapa2, ...(saved.etapa2 ?? {}) },
        documentos: [],
      };
    } catch {
      return initial;
    }
  });

  const [erros, setErros]       = useState<Record<string, string>>({});
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [filiacaoId, setFiliacaoId] = useState<string | null>(null);
  const [affiliateIdGerado, setAffiliateIdGerado] = useState<string | null>(null);
  const [systemIdGerado, setSystemIdGerado] = useState<string | null>(null);
  const [cepLoading, setCepLoading] = useState(false);
const systemIdRef = useRef<string | null>(null);
  const affiliateIdRef = useRef<string | null>(null);
  const formStartedRef = useRef(false);
  const pageViewSentRef = useRef(false);

  useEffect(() => {
    if (!pageViewSentRef.current) {
      pageViewSentRef.current = true;
      trackFiliationEvent({ eventType: 'page_view', section: 'pagina_filiacao' });
    }
    const handlePageExit = () => trackFiliationEvent({ eventType: 'page_exit', step: etapaAtual, section: 'pagina_filiacao' });
    window.addEventListener('pagehide', handlePageExit);
    return () => window.removeEventListener('pagehide', handlePageExit);
  }, [etapaAtual]);

  const possuiRascunho = Object.values(dados.etapa1).some(value => String(value).trim())
    || Object.values(dados.etapa2).some(value => String(value).trim() && value !== 0);

  useEffect(() => {
    if (!possuiRascunho || filiacaoId) return;
    localStorage.setItem(FILIACAO_DRAFT_KEY, JSON.stringify({ etapa1: dados.etapa1, etapa2: dados.etapa2 }));
  }, [dados.etapa1, dados.etapa2, filiacaoId, possuiRascunho]);

  useEffect(() => {
    const alertarSaida = (event: BeforeUnloadEvent) => {
      if (!possuiRascunho || filiacaoId) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', alertarSaida);
    return () => window.removeEventListener('beforeunload', alertarSaida);
  }, [filiacaoId, possuiRascunho]);

  useEffect(() => {
    const sections = { 1: 'identificacao', 2: 'dados_complementares', 3: 'documentos' } as const;
    trackFiliationEvent({ eventType: 'step_viewed', step: etapaAtual, section: sections[etapaAtual] });
  }, [etapaAtual]);

  const markFormStarted = (step: number) => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackFiliationEvent({ eventType: 'form_started', step, section: 'identificacao' });
  };
  const validarCNPJ = (cnpj: string): boolean => cnpj.replace(/\D/g, '').length === 14;
  const validarCPF  = (cpf: string):  boolean => cpf.replace(/\D/g, '').length  === 11;

  const buscarCEP = async (cep: string) => {
    const cepNum = cep.replace(/\D/g, '');
    if (cepNum.length !== 8) {
      setErros(p => ({ ...p, cep: 'CEP inválido (8 dígitos)' }));
      trackFiliationEvent({ eventType: 'cep_error', step: 1, section: 'endereco', errorCode: 'invalid_cep' });
      return;
    }
    try {
      setCepLoading(true);
      const res  = await fetch(`https://viacep.com.br/ws/${cepNum}/json/`);
      const data = await res.json();
      if (data?.erro) {
        setErros(p => ({ ...p, cep: 'CEP não encontrado' }));
        trackFiliationEvent({ eventType: 'cep_error', step: 1, section: 'endereco', errorCode: 'cep_not_found' });
        return;
      }
      setDados(p => ({
        ...p,
        etapa1: {
          ...p.etapa1,
          endereco: data.logradouro || p.etapa1.endereco,
          cidade:   data.localidade || p.etapa1.cidade,
          estado:   data.uf         || p.etapa1.estado,
        },
      }));
      setErros(p => ({ ...p, cep: '' }));
    } catch {
      setErros(p => ({ ...p, cep: 'Erro ao buscar CEP' }));
      trackFiliationEvent({ eventType: 'cep_error', step: 1, section: 'endereco', errorCode: 'cep_lookup_failed' });
    } finally { setCepLoading(false); }
  };

  const handleChangeEtapa1 = (field: keyof DadosEtapa1, value: string) => {
    markFormStarted(1);
    setDados(p => ({ ...p, etapa1: { ...p.etapa1, [field]: value } }));
    setErros(p => ({ ...p, [field]: '' }));
  };

  const handleChangeEtapa2 = (field: keyof DadosEtapa2, value: string | number) => {
    markFormStarted(2);
    setDados(p => ({ ...p, etapa2: { ...p.etapa2, [field]: value } }));
    setErros(p => ({ ...p, [field]: '' }));
  };

  const validarEtapa1 = (): boolean => {
    const novo: Record<string, string> = {};
    if (!dados.etapa1.nomeSolicitante.trim()) novo.nomeSolicitante = 'Nome obrigatório';
    if (!validarCPF(dados.etapa1.cpf))        novo.cpf = 'CPF inválido (11 dígitos)';
    if (dados.etapa1.cnpj.trim().length > 0 && !validarCNPJ(dados.etapa1.cnpj))
      novo.cnpj = 'CNPJ inválido (14 dígitos) ou deixe em branco';
    if (dados.etapa1.cep.replace(/\D/g, '').length !== 8) novo.cep = 'CEP inválido (8 dígitos)';
    if (!dados.etapa1.endereco.trim())        novo.endereco = 'Endereço obrigatório';
    if (!dados.etapa1.email.includes('@'))    novo.email    = 'E-mail inválido';
    if (dados.etapa1.identidadeInternacional && !/^[A-Z0-9]{15}$/.test(dados.etapa1.identidadeInternacional)) {
      novo.identidadeInternacional = 'Informe exatamente 15 letras ou números';
    }
    setErros(novo);
    if (Object.keys(novo).length > 0) {
      trackFiliationEvent({
        eventType: 'validation_error',
        step: 1,
        section: 'identificacao',
        errorCode: 'validation_failed',
        metadata: { fields: Object.keys(novo) },
      });
    }
    return Object.keys(novo).length === 0;
  };

  const validarEtapa2 = (): boolean => {
    const novo: Record<string, string> = {};
    if (dados.etapa2.experienciaAnos < 1)       novo.experienciaAnos  = 'Mínimo 1 ano';
    if (!dados.etapa2.especializacoes.trim())    novo.especializacoes  = 'Especializações obrigatórias';
    setErros(novo);
    if (Object.keys(novo).length > 0) {
      trackFiliationEvent({
        eventType: 'validation_error',
        step: 2,
        section: 'dados_complementares',
        errorCode: 'validation_failed',
        metadata: { fields: Object.keys(novo) },
      });
    }
    return Object.keys(novo).length === 0;
  };

  const submitFiliacao = async (): Promise<{ affiliateId: string; systemId: string }> => {
    const ok1 = validarEtapa1();
    const ok2 = validarEtapa2();
    if (!ok1 || !ok2) {
      const msg = 'Há campos obrigatórios não preenchidos. Volte às etapas anteriores e corrija os erros em vermelho.';
      setErroEnvio(msg);
      trackFiliationEvent({ eventType: 'registration_error', step: 3, section: 'cadastro', errorCode: 'registration_validation_failed' });
      throw new Error(msg);
    }

    // ✅ Payload completo — todos os campos do formulário
    const payload = {
      nomeSolicitante:     dados.etapa1.nomeSolicitante,
      cpf:                 dados.etapa1.cpf,
      cnpj:                dados.etapa1.cnpj            || undefined,
      email:               dados.etapa1.email,
      schoolName:          dados.etapa1.nomeFantasia     || dados.etapa1.nomeSolicitante,
      endereco:            dados.etapa1.endereco,
      complemento:         dados.etapa1.complemento      || undefined, // ✅ NOVO
      cep:                 dados.etapa1.cep,
      city:                dados.etapa1.cidade,
      state:               dados.etapa1.estado,
      phone:               dados.etapa1.telefone,
      dataNascimento:      dados.etapa1.dataNascimento   || undefined,
      identidadeInternacional: dados.etapa1.identidadeInternacional || undefined,
      funcao:              dados.etapa2.funcao,
      experienciaAnos:     dados.etapa2.experienciaAnos,
      dataInicioAtividade: dados.etapa2.dataInicioAtividade || undefined, // ✅ NOVO
      especializacoes:     dados.etapa2.especializacoes,
    };

    setErroEnvio(null);
    const response = await fetch(`${API_BASE}/affiliates/register`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
      trackFiliationEvent({
        eventType: 'registration_error',
        step: 3,
        section: 'cadastro',
        errorCode: `http_${response.status}`,
      });
      throw new Error(error?.message || `Erro ao registrar filiação (${response.status})`);
    }

    const result = await response.json();
  setAffiliateIdGerado(result.id);
  affiliateIdRef.current = result.id;
  trackFiliationEvent({ eventType: 'registration_created', step: 3, section: 'cadastro', affiliateId: result.id });
  
setSystemIdGerado(result.systemId);
systemIdRef.current = result.systemId;
return { affiliateId: result.id, systemId: result.systemId };
  };

  const proxima = () => {
    if (etapaAtual === 1 && validarEtapa1()) {
      trackFiliationEvent({ eventType: 'step_completed', step: 1, section: 'identificacao' });
      setEtapaAtual(2);
    }
    if (etapaAtual === 2 && validarEtapa2()) {
      trackFiliationEvent({ eventType: 'step_completed', step: 2, section: 'dados_complementares' });
      setEtapaAtual(3);
    }
  };

  const anterior = () => {
    if (etapaAtual > 1) setEtapaAtual((etapaAtual - 1) as 1 | 2 | 3);
  };

  const handleDocumentosSalvos = (arquivos: File[]) => {
    setDados(p => ({ ...p, documentos: arquivos }));
  };
const handleUploadSucesso = (results: { id: string; url: string }[]) => {
  const id = systemIdRef.current ?? systemIdGerado;
  if (id) {
    setFiliacaoId(id);
  } else {
    setFiliacaoId('FIL-' + Math.random().toString(36).slice(2, 11).toUpperCase());
  }
  finishFiliationTracking(affiliateIdRef.current || affiliateIdGerado || undefined);
  localStorage.removeItem(FILIACAO_DRAFT_KEY);
};
  if (filiacaoId) {
    return <ConfirmacaoFiliacao dados={dados} filiacaoId={filiacaoId} />;
  }

  const StepPill = ({ n, label }: { n: number; label: string }) => {
    const active = etapaAtual >= (n as 1 | 2 | 3);
    return (
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center font-black text-sm border"
          style={{
            backgroundColor: active ? BRAND.charcoal : 'white',
            color:            active ? 'white' : BRAND.charcoal,
            borderColor:      active ? BRAND.charcoal : `${BRAND.gold}55`,
          }}>
          {n}
        </div>
        <span className={`text-sm ${active ? 'font-bold text-gray-900' : 'text-gray-500'}`}>{label}</span>
      </div>
    );
  };

  const inputBase = 'w-full px-4 py-3 rounded-xl bg-white text-gray-900 border focus:outline-none focus:ring-2 focus:ring-offset-0';

  return (
    <div className="space-y-6">
      {/* Progresso */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <StepPill n={1} label="Identificação" />
          <div className="h-[2px] w-10 bg-gray-200 hidden md:block" />
          <StepPill n={2} label="Complementar" />
          <div className="h-[2px] w-10 bg-gray-200 hidden md:block" />
          <StepPill n={3} label="Documentos" />
        </div>
        <div className="text-sm text-gray-600">
          Etapa <span className="font-bold text-gray-900">{etapaAtual}</span> de 3
        </div>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-100" aria-label={`Progresso: etapa ${etapaAtual} de 3`}>
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${(etapaAtual / 3) * 100}%`, background: `linear-gradient(90deg, ${BRAND.goldDeep}, ${BRAND.gold})` }}
        />
      </div>
      {possuiRascunho && (
        <p className="text-xs text-emerald-700 font-semibold">✓ Seu progresso está sendo salvo automaticamente neste dispositivo.</p>
      )}

      {/* Card */}
      <div className="rounded-2xl border bg-white/80 backdrop-blur p-6 md:p-7 shadow-sm"
        style={{ borderColor: `${BRAND.gold}33` }}>

        {/* ── ETAPA 1 ─────────────────────────────────────────── */}
        {etapaAtual === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-black" style={{ color: BRAND.charcoal }}>
                Identificação do solicitante
              </h3>
              <p className="text-sm text-gray-600 mt-1">CPF é obrigatório. CNPJ é opcional.</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nome completo *</label>
              <input type="text" value={dados.etapa1.nomeSolicitante}
                onChange={e => handleChangeEtapa1('nomeSolicitante', e.target.value)}
                placeholder="Ex: Maria Silva"
                className={`${inputBase} ${erros.nomeSolicitante ? 'border-red-500' : 'border-gray-200'}`} />
              {erros.nomeSolicitante && <p className="text-red-500 text-sm mt-1">{erros.nomeSolicitante}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">CPF *</label>
                <input type="text" value={dados.etapa1.cpf}
                  onChange={e => handleChangeEtapa1('cpf', e.target.value.replace(/\D/g, '').slice(0, 11))}
                  placeholder="Somente números"
                  className={`${inputBase} ${erros.cpf ? 'border-red-500' : 'border-gray-200'}`} />
                {erros.cpf && <p className="text-red-500 text-sm mt-1">{erros.cpf}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">CNPJ (opcional)</label>
                <input type="text" value={dados.etapa1.cnpj}
                  onChange={e => handleChangeEtapa1('cnpj', e.target.value.replace(/\D/g, '').slice(0, 14))}
                  placeholder="Se for empresa, informe o CNPJ"
                  className={`${inputBase} ${erros.cnpj ? 'border-red-500' : 'border-gray-200'}`} />
                {erros.cnpj && <p className="text-red-500 text-sm mt-1">{erros.cnpj}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nome fantasia (opcional)</label>
              <input type="text" value={dados.etapa1.nomeFantasia}
                onChange={e => handleChangeEtapa1('nomeFantasia', e.target.value)}
                placeholder="Ex: Studio Beleza Premium"
                className={`${inputBase} border-gray-200`} />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Data de nascimento</label>
              <input type="date" value={dados.etapa1.dataNascimento}
                onChange={e => handleChangeEtapa1('dataNascimento', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className={`${inputBase} border-gray-200`} />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Identidade internacional (opcional)</label>
              <input
                type="text"
                value={dados.etapa1.identidadeInternacional}
                onChange={e => handleChangeEtapa1(
                  'identidadeInternacional',
                  e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 15),
                )}
                placeholder="AB12CD34EF56GH7"
                maxLength={15}
                className={`${inputBase} font-mono ${erros.identidadeInternacional ? 'border-red-500' : 'border-gray-200'}`}
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-gray-500">Exatamente 15 caracteres, somente letras e números</p>
                <p className="text-xs text-gray-400 font-mono">{dados.etapa1.identidadeInternacional.length}/15</p>
              </div>
              {erros.identidadeInternacional && <p className="text-red-500 text-sm mt-1">{erros.identidadeInternacional}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">CEP *</label>
              <div className="flex gap-3">
                <input type="text" value={dados.etapa1.cep}
                  onChange={e => handleChangeEtapa1('cep', e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="00000-000"
                  className={`${inputBase} flex-1 ${erros.cep ? 'border-red-500' : 'border-gray-200'}`} />
                <button type="button" onClick={() => buscarCEP(dados.etapa1.cep)}
                  className="px-4 py-3 rounded-xl font-bold border flex items-center gap-2"
                  style={{ borderColor: `${BRAND.gold}55`, color: BRAND.charcoal, backgroundColor: 'white' }}>
                  <Search className="w-4 h-4" style={{ color: BRAND.goldDeep }} />
                  {cepLoading ? 'Buscando...' : 'Buscar'}
                </button>
              </div>
              {erros.cep && <p className="text-red-500 text-sm mt-1">{erros.cep}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Endereço *</label>
              <input type="text" value={dados.etapa1.endereco}
                onChange={e => handleChangeEtapa1('endereco', e.target.value)}
                placeholder="Rua, Avenida..."
                className={`${inputBase} ${erros.endereco ? 'border-red-500' : 'border-gray-200'}`} />
              {erros.endereco && <p className="text-red-500 text-sm mt-1">{erros.endereco}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Cidade</label>
                <input type="text" value={dados.etapa1.cidade}
                  onChange={e => handleChangeEtapa1('cidade', e.target.value)}
                  className={`${inputBase} border-gray-200`} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Estado</label>
                <select value={dados.etapa1.estado}
                  onChange={e => handleChangeEtapa1('estado', e.target.value)}
                  className={`${inputBase} border-gray-200 bg-white`}>
                  <option value="">Selecione…</option>
                  {ESTADOS_BR.map(({ uf, nome }) => (
                    <option key={uf} value={uf}>{nome} ({uf})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Complemento</label>
                <input type="text" value={dados.etapa1.complemento}
                  onChange={e => handleChangeEtapa1('complemento', e.target.value)}
                  placeholder="Apto, Sala..."
                  className={`${inputBase} border-gray-200`} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">E-mail *</label>
                <input type="email" value={dados.etapa1.email}
                  onChange={e => handleChangeEtapa1('email', e.target.value)}
                  placeholder="seuemail@dominio.com"
                  className={`${inputBase} ${erros.email ? 'border-red-500' : 'border-gray-200'}`} />
                {erros.email && <p className="text-red-500 text-sm mt-1">{erros.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Telefone</label>
                <input type="text" value={dados.etapa1.telefone}
                  onChange={e => handleChangeEtapa1('telefone', e.target.value)}
                  placeholder="(00) 00000-0000"
                  className={`${inputBase} border-gray-200`} />
              </div>
            </div>
          </div>
        )}

        {/* ── ETAPA 2 ─────────────────────────────────────────── */}
        {etapaAtual === 2 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-black" style={{ color: BRAND.charcoal }}>
                Dados complementares
              </h3>
              <p className="text-sm text-gray-600 mt-1">Conte sobre sua atuação para facilitar a análise.</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Função/Atuação</label>
              <input type="text" value={dados.etapa2.funcao}
                onChange={e => handleChangeEtapa2('funcao', e.target.value)}
                placeholder="Ex: Cabeleireiro, Instrutor, Empresário..."
                className={`${inputBase} border-gray-200`} />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Data de início na área</label>
              <input type="date" value={dados.etapa2.dataInicioAtividade}
                onChange={e => handleChangeEtapa2('dataInicioAtividade', e.target.value)}
                className={`${inputBase} border-gray-200`} />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Anos de experiência *</label>
              <input type="number" value={dados.etapa2.experienciaAnos || ''}
                onChange={e => handleChangeEtapa2('experienciaAnos', parseInt(e.target.value) || 0)}
                className={`${inputBase} ${erros.experienciaAnos ? 'border-red-500' : 'border-gray-200'}`} />
              {erros.experienciaAnos && <p className="text-red-500 text-sm mt-1">{erros.experienciaAnos}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Especializações *</label>
              <textarea value={dados.etapa2.especializacoes}
                onChange={e => handleChangeEtapa2('especializacoes', e.target.value)}
                rows={4}
                className={`${inputBase} resize-none ${erros.especializacoes ? 'border-red-500' : 'border-gray-200'}`} />
              {erros.especializacoes && <p className="text-red-500 text-sm mt-1">{erros.especializacoes}</p>}
            </div>
          </div>
        )}

        {/* ── ETAPA 3 ─────────────────────────────────────────── */}
        {etapaAtual === 3 && (
          <UploadDocumentos
            affiliateId={affiliateIdGerado}
            onDocumentosSalvos={handleDocumentosSalvos}
            onUploadSucesso={handleUploadSucesso}
            onGerarAffiliateId={submitFiliacao}
            onTrackingError={(code, section, metadata) => trackFiliationEvent({
              eventType: 'upload_error',
              step: 3,
              section,
              errorCode: code,
              metadata,
            })}
          />
        )}
      </div>

      {/* Banner de erro da etapa 3 (erros nas etapas anteriores ou backend) */}
      {etapaAtual === 3 && erroEnvio && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-red-300 bg-red-50 text-red-700 text-sm">
          <span className="text-lg leading-none">⚠️</span>
          <div>
            <p className="font-bold">Atenção</p>
            <p>{erroEnvio}</p>
          </div>
        </div>
      )}

      {/* Navegação */}
      <div className="flex items-center justify-between">
        {etapaAtual > 1 ? (
          <button type="button" onClick={anterior}
            className="px-5 py-3 rounded-xl font-bold border flex items-center gap-2 hover:opacity-90 transition"
            style={{ borderColor: `${BRAND.gold}55`, color: BRAND.charcoal, backgroundColor: 'white' }}>
            <ChevronLeft className="w-5 h-5" style={{ color: BRAND.goldDeep }} />
            Voltar
          </button>
        ) : <div />}
        {etapaAtual < 3 && (
          <button type="button" onClick={proxima}
            className="px-6 py-3 rounded-xl font-black flex items-center gap-2 shadow-lg hover:shadow-xl transition"
            style={{ background: `linear-gradient(90deg, ${BRAND.goldDeep}, ${BRAND.gold})`, color: BRAND.charcoal }}>
            Próxima
            <ChevronRight className="w-5 h-5" style={{ color: BRAND.charcoal }} />
          </button>
        )}
      </div>
    </div>
  );
};

export default FormularioFiliacaoPublico;
